import React, { useEffect, useState } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const STATUS_BADGE = {
  open:      'badge--blue',
  accepted:  'badge--green',
  rejected:  'badge--danger',
  completed: 'badge--success',
  cancelled: 'badge--gray',
};
const STATUS_LABEL = {
  open:'Open', accepted:'Accepted', rejected:'Rejected',
  completed:'Completed', cancelled:'Cancelled',
};

export default function CropRequestsPage() {
  const [requests, setRequests] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState('');
  const [status,   setStatus]   = useState('');
  const [page,     setPage]     = useState(1);
  const [total,    setTotal]    = useState(0);
  const [selected, setSelected] = useState(null);

  useEffect(() => { load(); }, [search, status, page]);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (search) params.set('search', search);
      if (status) params.set('status', status);
      const { data } = await api.get('/requests/admin?' + params);
      setRequests(data.requests || []);
      setTotal(data.total || 0);
    } catch {
      toast.error('Failed to load requests');
    } finally { setLoading(false); }
  };

  const updateStatus = async (id, newStatus) => {
    try {
      await api.put(`/requests/${id}/status`, { status: newStatus });
      toast.success('Status updated ✅');
      load();
      setSelected(null);
    } catch { toast.error('Error updating status'); }
  };

  return (
    <AdminLayout title="Crop Requests">

      {/* Filters */}
      <div style={{ display:'flex', gap:8, marginBottom:16, flexWrap:'wrap' }}>
        <input className="input" placeholder="Search crop name, buyer..." value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }} style={{ width:220 }} />
        <select className="input" value={status}
          onChange={e => { setStatus(e.target.value); setPage(1); }} style={{ width:150 }}>
          <option value="">All Status</option>
          <option value="open">Open</option>
          <option value="accepted">Accepted</option>
          <option value="rejected">Rejected</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <div style={{ marginLeft:'auto', fontSize:12, color:'#6C757D', alignSelf:'center' }}>
          {total} total requests
        </div>
      </div>

      {/* Table */}
      <div className="card">
        {loading ? <div className="page-loader"><span className="spinner spinner--md" /></div> : (
          <div className="table-wrap">
            <table>
              <thead><tr>
                <th>Crop</th>
                <th>Buyer / Agent</th>
                <th>Quantity</th>
                <th>Price Offered</th>
                <th>Location</th>
                <th>Status</th>
                <th>Date</th>
                <th>Actions</th>
              </tr></thead>
              <tbody>
                {requests.length === 0 ? (
                  <tr><td colSpan={8}>
                    <div className="empty-state">
                      <div className="empty-state__icon">📦</div>
                      <div className="empty-state__title">No crop requests found</div>
                      <div className="empty-state__desc">Requests from buyers and agents appear here</div>
                    </div>
                  </td></tr>
                ) : requests.map(r => (
                  <tr key={r._id}>
                    <td>
                      <div style={{ fontWeight:600, fontSize:13 }}>{r.cropName}</div>
                      <div style={{ fontSize:11, color:'#6C757D', textTransform:'capitalize' }}>
                        {r.category}{r.subCategory ? ' · ' + r.subCategory : ''}
                      </div>
                    </td>
                    <td>
                      <div style={{ fontWeight:500, fontSize:13 }}>
                        {r.requesterId?.name || '—'}
                      </div>
                      <div style={{ fontSize:11, color:'#2563EB' }}>
                        +91 {r.requesterId?.mobile}
                      </div>
                      <div style={{ fontSize:11, color:'#6C757D', textTransform:'capitalize' }}>
                        {r.requesterRole}
                      </div>
                    </td>
                    <td style={{ fontSize:13 }}>
                      {r.quantity} {r.quantityUnit}
                    </td>
                    <td style={{ fontSize:13 }}>
                      {r.offeredPrice
                        ? `₹${r.offeredPrice} ${r.offeredPriceUnit?.replace('per_','/')}`
                        : '—'}
                    </td>
                    <td style={{ fontSize:12, color:'#6C757D' }}>
                      {r.preferredLocation || '—'}
                    </td>
                    <td>
                      <span className={'badge ' + (STATUS_BADGE[r.status] || 'badge--gray')}>
                        {STATUS_LABEL[r.status] || r.status}
                      </span>
                    </td>
                    <td style={{ fontSize:12 }}>
                      {new Date(r.createdAt).toLocaleDateString('en-IN')}
                    </td>
                    <td>
                      <button className="btn btn--outline btn--sm" onClick={() => setSelected(r)}>
                        👁 View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {total > 20 && (
        <div className="pagination">
          <button className="btn btn--outline btn--sm" disabled={page===1} onClick={() => setPage(p=>p-1)}>← Prev</button>
          <span className="page-info">Page {page} of {Math.ceil(total/20)} · {total} requests</span>
          <button className="btn btn--outline btn--sm" disabled={page>=Math.ceil(total/20)} onClick={() => setPage(p=>p+1)}>Next →</button>
        </div>
      )}

      {/* Detail modal */}
      {selected && (
        <div className="modal-overlay" onClick={() => setSelected(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal__header">
              <div className="modal__title">📦 {selected.cropName}</div>
              <button className="modal__close" onClick={() => setSelected(null)}>×</button>
            </div>
            <div className="modal__body">
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
                {[
                  ['Buyer/Agent',    (selected.requesterId?.name || '—') + ' (' + selected.requesterRole + ')'],
                  ['Mobile',         '+91 ' + (selected.requesterId?.mobile || '—')],
                  ['Crop',           selected.cropName],
                  ['Category',       (selected.category || '—') + (selected.subCategory ? ' / ' + selected.subCategory : '')],
                  ['Quantity',       selected.quantity + ' ' + selected.quantityUnit],
                  ['Price Offered',  selected.offeredPrice ? '₹' + selected.offeredPrice + ' ' + (selected.offeredPriceUnit || '') : '—'],
                  ['Location Pref',  selected.preferredLocation || '—'],
                  ['Status',         STATUS_LABEL[selected.status] || selected.status],
                  ['Date',           new Date(selected.createdAt).toLocaleDateString('en-IN')],
                ].map(([k,v]) => (
                  <div key={k} style={{ background:'#f8f9fa', borderRadius:6, padding:'10px 12px' }}>
                    <div style={{ fontSize:11, color:'#6C757D', marginBottom:2 }}>{k}</div>
                    <div style={{ fontSize:13, fontWeight:600 }}>{String(v)}</div>
                  </div>
                ))}
              </div>
              {selected.notes && (
                <div style={{ background:'#fff8e1', borderRadius:8, padding:'10px 12px', marginBottom:12, fontSize:13, color:'#6C757D' }}>
                  📝 {selected.notes}
                </div>
              )}
              {/* Status actions */}
              {selected.status === 'open' && (
                <div style={{ display:'flex', gap:8 }}>
                  <button className="btn btn--success" style={{ flex:1 }}
                    onClick={() => updateStatus(selected._id, 'accepted')}>✅ Accept</button>
                  <button className="btn btn--danger" style={{ flex:1 }}
                    onClick={() => updateStatus(selected._id, 'rejected')}>❌ Reject</button>
                  <button className="btn btn--outline" style={{ flex:1 }}
                    onClick={() => updateStatus(selected._id, 'cancelled')}>🚫 Cancel</button>
                </div>
              )}
              {selected.status === 'accepted' && (
                <button className="btn btn--primary" style={{ width:'100%' }}
                  onClick={() => updateStatus(selected._id, 'completed')}>
                  ✅ Mark Completed
                </button>
              )}
            </div>
            <div className="modal__footer">
              <button className="btn btn--outline" onClick={() => setSelected(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}